import { createSelector } from 'reselect';
import { cacheKey } from './reducer';
import { getSegmentById, getSegments, getTripType } from '../currentSearch/selectors';
import { Config, DateUtils, FareUtils } from '../../utils';
import { CalendarType, TripType } from '../../models';

const getDate = (_, { date }) => date;
const getMonth = (_, { date }) => date.getMonth();
const getYear = (_, { date }) => date.getFullYear();
const getCalendarType = (_, { calendarType }) => calendarType;

const getPercentagePrice = (price, tripType) =>
    tripType === TripType.ONE_WAY ? price : price * FareUtils.PERCENTAGE;

const getFlexibleDates = ({ flexibleDates }) => flexibleDates;

const getCacheKey = createSelector(
    [getSegments, getTripType],
    (segments, tripType) => {
        const { origin, destination } = segments[0];
        return (origin && destination && cacheKey(origin, destination, tripType)) || null;
    }
);

const getData = createSelector(
    [getCacheKey, getFlexibleDates],
    (cacheKey, flexibleDates) => (flexibleDates[cacheKey] && flexibleDates[cacheKey].data) || {},
);

const getStatus = createSelector(
    [getCacheKey, getFlexibleDates],
    (cacheKey, flexibleDates) => (flexibleDates[cacheKey] && flexibleDates[cacheKey].status) || {},
);

const getDepartureDate = createSelector(
    [getSegmentById],
    (segment) => segment && DateUtils.toString(segment.date),
);

const reduceIfNaN = (objectOrNumber) =>
    Number.isNaN(Number(objectOrNumber)) ? Math.min(...Object.values(objectOrNumber)) : objectOrNumber;
const extractPrices = (data) =>
    Object.entries(data).reduce((acc, [key, values]) => {
        acc[key] = reduceIfNaN(values);
        return acc;
    }, {});

export const getDeparturePrices = createSelector([getData], (data) => extractPrices(data));

export const getReturnPrices = createSelector(
    [getData, getDepartureDate, getTripType],
    (data, departure, tripType) => (tripType === TripType.ROUND_TRIP ? data[departure] || {} : {}),
);

const getFaresData = createSelector(
    [getSegments, getFlexibleDates, getTripType],
    (segments, flexibleDates, tripType) => {
        let faresData;
        const { origin, destination } = segments[0];
        if (origin && destination) {
            const key = cacheKey(origin, destination, tripType);
            faresData = flexibleDates[key] && flexibleDates[key].data;
        }
        return faresData;
    },
);

const getFaresList = createSelector(
    [getFaresData, getSegments, getTripType],
    (faresData, segments, tripType) => {
        let list;
        if (faresData) {
            if (tripType === TripType.ONE_WAY) {
                list = faresData;
            } else if (tripType === TripType.ROUND_TRIP && segments[0].date) {
                list = faresData[DateUtils.toString(segments[0].date)];
            }
        }
        return list;
    },
);

export const getDepartureFare = createSelector(
    [getFaresData, getSegments, getTripType],
    (faresData, segments, tripType) => {
        let fare;
        if (faresData) {
            const { date } = segments[0];
            fare = faresData[DateUtils.toString(date)];
            if (tripType === TripType.ROUND_TRIP && fare) {
                fare = Math.min(...Object.values(fare)) * FareUtils.PERCENTAGE;
            }
        }
        return fare || 0;
    },
);

export const getReturnFare = createSelector(
    [getFaresData, getSegments, getDepartureFare],
    (faresData, segments, departureFare) => {
        let fare;
        if (faresData) {
            const list = faresData[DateUtils.toString(segments[0].date)];
            if (list && segments[1]) {
                const returnFare = list[DateUtils.toString(segments[1].date)];
                if (returnFare) {
                    fare = returnFare - departureFare;
                }
            }
        }
        return fare || 0;
    },
);

export const getCheapestPriceFromCurrentSearch = createSelector([getFaresList], (list) =>
    list ? Math.min(...Object.values(list)) : 0,
);

export const getCheapestPriceShownInDeparture = createSelector(
    [getFaresData, getTripType],
    (faresData, tripType) => {
        if (!faresData) {
            return 0;
        }
        const list = tripType === TripType.ONE_WAY ? faresData : extractPrices(faresData);
        const min = Math.min(...Object.values(list));
        return getPercentagePrice(min, tripType);
    },
);

export const getExpensivePriceShownInDeparture = createSelector(
    [getFaresData, getTripType],
    (faresData, tripType) => {
        if (!faresData) {
            return 0;
        }
        const list = tripType === TripType.ONE_WAY ? faresData : extractPrices(faresData);
        const max = Math.max(...Object.values(list));
        return getPercentagePrice(max, tripType);
    },
);

export const getCheapestPriceShownInReturn = createSelector(
    [getFaresList, getTripType, getDepartureFare],
    (list, tripType, departureFare) => {
        if (!list) {
            return 0;
        }
        const min = Math.min(...Object.values(list));
        return tripType === TripType.ONE_WAY ? min : min - departureFare;
    },
);

export const getExpensivePriceShownInReturn = createSelector(
    [getFaresList, getTripType, getDepartureFare],
    (list, tripType, departureFare) => {
        if (!list) {
            return 0;
        }
        const max = Math.max(...Object.values(list));
        return tripType === TripType.ONE_WAY ? max : max - departureFare;
    },
);

export const getNumberOfDepDaysWithFares = createSelector([getFaresData], (faresData) =>
    faresData ? Object.values(faresData).length : 0,
);

export const getNumberOfRetDaysWithFares = createSelector(
    [getFaresData, getSegments, getTripType],
    (faresData, segments, tripType) => {
        if (!faresData) {
            return 0;
        }
        let list;
        if (tripType === TripType.ROUND_TRIP) {
            list = faresData[DateUtils.toString(segments[0].date)];
        }
        return list ? Object.values(list).length : 0;
    },
);

const countDaysWithFares = (fares = {}) => {
    return Object.keys(fares).filter((value) => !DateUtils.isOutOfBounds(DateUtils.parse(value)))
        .map((value) => value.substring(0, 7)).reduce((prev, cur) => {
            prev[cur] = (prev[cur] || 0) + 1;
            return prev;
        }, {});
};

const extractFaresDataDeparture = (faresData, tripType) => {
    let fares = {};
    if (faresData) {
        fares = TripType.ONE_WAY === tripType ? faresData : extractPrices(faresData);
    }
    return fares;
};

const extractFaresDataReturn = (faresData, tripType, segments) => {
    let fares = {};
    if (TripType.ROUND_TRIP === tripType && faresData && segments[0].date) {
        fares = faresData[DateUtils.toString(segments[0].date)];
    }
    return fares;
};

export const getMonthlyDaysWithFaresDeparture = createSelector(
    [getFaresData, getTripType],
    (faresData, tripType) => {
        const fares = extractFaresDataDeparture(faresData, tripType);
        return countDaysWithFares(fares);
    },
);

export const getMonthlyDaysWithFaresReturn = createSelector(
    [getFaresData, getTripType, getSegments],
    (faresData, tripType, segments) => {
        const fares = extractFaresDataReturn(faresData, tripType, segments);
        return countDaysWithFares(fares);
    },
);

const getGreenDaysFromList = (data = {}, tripType) => {
    return Object.entries(data)
        .filter((el) => {
            const priceDate = new Date(el[0]);
            const price = Math.ceil(getPercentagePrice(el[1], tripType));
            const minPriceMonth = Math.ceil(getPercentagePrice(getMinMonthPrice(priceDate.getMonth(), priceDate.getFullYear(), data), tripType));
            return price === minPriceMonth;
        })
        .map((s) => s[0]);
};

export const getCheapestDepartureDaysList = createSelector(
    [getFaresData, getTripType],
    (faresData, tripType) => {
        const fares = extractFaresDataDeparture(faresData, tripType);
        return getGreenDaysFromList(fares, tripType);
    },
);

export const getCheapestReturnDaysList = createSelector(
    [getFaresData, getTripType, getSegments],
    (faresData, tripType, segments) => {
        const fares = extractFaresDataReturn(faresData, tripType, segments);
        return getGreenDaysFromList(fares, tripType);
    },
);

export const getAveragePriceShownDeparture = createSelector(
    [getFaresData, getTripType],
    (faresData, tripType) => {
        if (!faresData) {
            return 0;
        }
        const list = tripType === TripType.ONE_WAY ? faresData : extractPrices(faresData);
        const priceList = Object.values(list);
        return (
            priceList.reduce((prev, curr) => prev + getPercentagePrice(curr, tripType), 0) /
            priceList.length
        );
    },
);

export const getAveragePriceShownReturn = createSelector(
    [getFaresList, getTripType, getDepartureFare],
    (faresList, tripType, departureFare) => {
        if (!faresList) {
            return 0;
        }
        const list = tripType === TripType.ONE_WAY ? {} : faresList;
        const priceList = Object.values(list);
        return (
            priceList.reduce((prev, curr) => prev + (curr - departureFare), 0) / priceList.length ||
            0
        );
    },
);

export const dayPricesLoaded = createSelector(
    [getStatus, getDate],
    (status, date) => {
        const dayInMS = 24 * 60 * 60 * 1000;
        const daysWithFares = Config.get('daysWithFares');
        const cacheValidTimestamp = daysWithFares * dayInMS;
        const timestamp = date.getTime();

        const requestStatus = Object.keys(status)
            .map((key) => parseInt(key))
            .find((key) => key <= timestamp && key + cacheValidTimestamp > timestamp);
        return !!requestStatus && status[requestStatus] === 'loaded';
    },
);

const getDisplayDeparturePrices = createSelector(
    [getDeparturePrices, getTripType],
    (departurePrices, tripType) => {
        return Object.keys(departurePrices).reduce(
            (acc, key) => ({
                ...acc,
                [key]: getPercentagePrice(departurePrices[key], tripType),
            }),
            {},
        );
    },
);

const getCalendarTypePrices = createSelector(
    [getCalendarType, getDisplayDeparturePrices, getReturnPrices],
    (calendarType, displayDeparturePrices, returnPrices) => {
        return calendarType === CalendarType.DEPARTURE ? displayDeparturePrices : returnPrices;
    },
);

const getMinMonthPrice = (month, year, prices) => {
    const minMonthPrice = prices
        ? Object.entries(prices)
            .filter(([dateKey]) => {
                const priceDate = new Date(dateKey);
                const priceMonth = priceDate.getMonth();
                const priceYear = priceDate.getFullYear();
                return month === priceMonth && year === priceYear;
            })
            .reduce((min, [_, price]) => Math.min(min, price), Infinity)
        : Infinity;
    return Math.ceil(minMonthPrice);
};

const getCheapestPriceInMonth = createSelector(
    [getMonth, getYear, getCalendarTypePrices],
    (month, year, prices) => getMinMonthPrice(month, year, prices),
);

const getDatePrice = createSelector([getDate, getCalendarTypePrices], (date, prices) => {
    const dateStr = DateUtils.toString(date);
    return Math.ceil(prices[dateStr]);
});

export const isCheapestPriceInMonth = createSelector(
    [getDatePrice, getCheapestPriceInMonth],
    (datePrice, cheapestPrice) => datePrice === cheapestPrice,
);

const getCheapestDaysFromList = (data) => {
    return Object.entries(data)
        .filter((el) => {
            const priceDate = new Date(el[0]);
            const price = Math.ceil(el[1]);
            return price === getMinMonthPrice(priceDate.getMonth(), priceDate.getFullYear(), data);
        })
        .map((s) => s[0]);
};
export const getCheapestDepartureDays = createSelector([getDisplayDeparturePrices], (data) =>
    getCheapestDaysFromList(data),
);

export const getCheapestReturnDays = createSelector([getReturnPrices], (data) =>
    getCheapestDaysFromList(data),
);

export const lastDateRequested = createSelector(
    [getCacheKey, getFlexibleDates],
    (cacheKey, flexibleDates) => {
        const status = (flexibleDates[cacheKey] && flexibleDates[cacheKey].status) || {};
        const daysWithFares = Config.get('daysWithFares');
        const dayInMS = 24 * 60 * 60 * 1000;
        const cacheValidTimestamp = daysWithFares * dayInMS;
        const l = Object.entries(status).filter((a) => a[1] === 'loaded');
        return l.length
            ? parseInt(l[l.length - 1][0]) + cacheValidTimestamp
            : DateUtils.getToday().getTime();
    },
);
